import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Locations, Users, Auth } from "../lib/api";

const AppContext = createContext(null);

const LS_TOKEN = "od360_token";
const LS_USER = "od360_active_user_id";   // legacy key reused for audit interceptor
const LS_LOCS = "od360_active_location_ids";

/**
 * AppContext now owns auth too:
 * - `token` + `activeUser` come from `/auth/me` after login
 * - `login(mobile, password)` / `logout()` exposed for the Login page & TopBar
 * - `mustChangePassword` is true on first login (or after admin reset) and the
 *    app shows a blocking dialog until the user changes it.
 */
export function AppProvider({ children }) {
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);            // admin directory only
  const [activeUser, setActiveUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem(LS_TOKEN) || null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [activeLocationIds, setActiveLocationIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authReady, setAuthReady] = useState(false);

  // ---------- directory (locations + users list for admin/audit lookup) ----------
  const refreshDirectory = useCallback(async () => {
    const [locs, usrs] = await Promise.all([
      Locations.list().catch(() => []),
      Users.list().catch(() => []),
    ]);
    setLocations(locs);
    setUsers(usrs);
    return { locs, usrs };
  }, []);

  // ---------- bootstrap: validate stored token & hydrate user ----------
  useEffect(() => {
    (async () => {
      const t = localStorage.getItem(LS_TOKEN);
      if (!t) {
        setAuthReady(true);
        setLoading(false);
        return;
      }
      try {
        const me = await Auth.me();
        setActiveUser(me);
        localStorage.setItem(LS_USER, me.id);
        await refreshDirectory();
        const savedLocs = JSON.parse(localStorage.getItem(LS_LOCS) || "null");
        if (Array.isArray(savedLocs) && savedLocs.length) {
          setActiveLocationIds(savedLocs);
        } else if (me?.location_ids?.length) {
          setActiveLocationIds(me.location_ids);
        }
      } catch {
        // Token invalid/expired — clear and force re-login.
        localStorage.removeItem(LS_TOKEN);
        localStorage.removeItem(LS_USER);
        setToken(null);
        setActiveUser(null);
      } finally {
        setAuthReady(true);
        setLoading(false);
      }
    })();
  }, [refreshDirectory]);

  // ---------- listen for 401s from axios interceptor ----------
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem(LS_TOKEN);
      localStorage.removeItem(LS_USER);
      setToken(null);
      setActiveUser(null);
      setMustChangePassword(false);
    };
    window.addEventListener("od360-auth-expired", handler);
    return () => window.removeEventListener("od360-auth-expired", handler);
  }, []);

  // ---------- auth helpers ----------
  const login = useCallback(async (mobile, password) => {
    const res = await Auth.login({ mobile, password });
    localStorage.setItem(LS_TOKEN, res.token);
    localStorage.setItem(LS_USER, res.user.id);
    setToken(res.token);
    setActiveUser(res.user);
    setMustChangePassword(!!res.must_change_password);
    // hydrate directory + default location filter
    const { locs } = await refreshDirectory();
    if (res.user.location_ids?.length) {
      setActiveLocationIds(res.user.location_ids);
      localStorage.setItem(LS_LOCS, JSON.stringify(res.user.location_ids));
    } else {
      setActiveLocationIds(locs.map((l) => l.id));
    }
    return res;
  }, [refreshDirectory]);

  const logout = useCallback(() => {
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_USER);
    localStorage.removeItem(LS_LOCS);
    setToken(null);
    setActiveUser(null);
    setMustChangePassword(false);
    setActiveLocationIds([]);
  }, []);

  const setLocationIds = (ids) => {
    setActiveLocationIds(ids);
    localStorage.setItem(LS_LOCS, JSON.stringify(ids));
  };

  const value = {
    loading,
    authReady,
    token,
    locations,
    users,
    activeUser,
    activeRole: activeUser?.role || null,
    activeLocationIds,
    mustChangePassword,
    setMustChangePassword,
    login,
    logout,
    setLocationIds,
    refreshDirectory,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
